import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DataState, MlModelWithSegmentationSettings, SupportTypes, WorkflowSimple } from '@ca/core/models';
import { UserService } from '@ca/core/services';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { map, tap } from 'rxjs/operators';
import { MLMeasurement } from 'src/app/core/models/measurement.model';
import { Selected } from 'src/app/core/models/selected.model';
import { buildViewModel } from 'src/app/core/utils/viewmodel';
import { ContactUsComponent } from 'src/app/shared/components/contact-us/contact-us.component';
import { CreateApplicationDialogFacadeService } from './services/create-application-dialog-facade.service';
import { UserProfileFacadeService } from '../../../user/services/user-profile-facade/user-profile-facade.service';
import { forkJoin } from 'rxjs';

export interface CreateApplicationDialogViewModel {
    hasPermissions$: boolean;
    measurementsState$: DataState<Selected<MLMeasurement>[]>;
    allMachineLearningModelsState$: DataState<MlModelWithSegmentationSettings[]>;
    workflowsState$: DataState<WorkflowSimple[]>;
}

@Component({
    selector: 'ca-create-application-dialog',
    templateUrl: './create-application-dialog.component.html',
    styleUrls: ['./create-application-dialog.component.scss'],
    providers: [CreateApplicationDialogFacadeService, UserProfileFacadeService],
})
export class CreateApplicationDialogComponent implements OnInit {
    @Input() trainingId?: string;

    vm$$ = buildViewModel<
        CreateApplicationDialogViewModel,
        boolean | DataState<Selected<MLMeasurement>[]> | DataState<MlModelWithSegmentationSettings[]> | DataState<WorkflowSimple[]>
    >({
        hasPermissions$: forkJoin([this.userProfileFacadeService.hasApplicationsPermissions$(), this.userProfileFacadeService.hasWorkflowsPermissions$()]).pipe(
            map(([p1, p2]) => p1 && p2),
        ),
        measurementsState$: this.createApplicationDialogFacadeService.measurementsState$.pipe(
            tap((measurementsState: DataState<Selected<MLMeasurement>[]>) => {
                if (measurementsState.data) {
                    this.selectedMeasurements = measurementsState.data
                        .filter((selectedMeasurement: Selected<MLMeasurement>) => selectedMeasurement.selected)
                        .map((selectedMeasurement: Selected<MLMeasurement>) => selectedMeasurement.child);
                }
            }),
        ),
        allMachineLearningModelsState$: this.createApplicationDialogFacadeService.allMachineLearningModelsState$.pipe(
            tap((allMachineLearningModelsState: DataState<MlModelWithSegmentationSettings[]>) => {
                if (allMachineLearningModelsState.data && this.trainingId) {
                    const modelToPreselect = allMachineLearningModelsState.data.find(
                        (model: MlModelWithSegmentationSettings) => model.trainingId === this.trainingId,
                    );
                    if (modelToPreselect) {
                        this.selectedModel = modelToPreselect;
                    }
                }
            }),
        ),
        workflowsState$: this.createApplicationDialogFacadeService.workflowsState$,
    });

    @Output() onApplicationDataSet = new EventEmitter<{
        applicationName: string;
        selectedModel: MlModelWithSegmentationSettings;
        selectedMeasurements: MLMeasurement[];
        selectedWorkflow?: WorkflowSimple;
        closeDialog: Function;
    }>();

    applicationName: string;
    selectedModel: MlModelWithSegmentationSettings;
    selectedWorkflow: WorkflowSimple;
    selectedMeasurements: MLMeasurement[] = [];
    useMyWorkflow: boolean = false;

    constructor(
        private activeModal: NgbActiveModal,
        private createApplicationDialogFacadeService: CreateApplicationDialogFacadeService,
        private userProfileFacadeService: UserProfileFacadeService,
        private modalService: NgbModal,
        private translateService: TranslateService,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.createApplicationDialogFacadeService.loadMeasurements();
        this.createApplicationDialogFacadeService.loadModels();
        this.createApplicationDialogFacadeService.loadWorkflows();
    }

    isUserOwner(ownerId: string): boolean {
        return this.userService.isCurrentUser(ownerId);
    }

    submit(): void {
        this.onApplicationDataSet.emit({
            applicationName: this.applicationName,
            selectedModel: this.selectedModel,
            selectedMeasurements: this.selectedMeasurements,
            ...(this.useMyWorkflow && this.selectedWorkflow && { selectedWorkflow: this.selectedWorkflow }),
            closeDialog: () => this.activeModal.close(),
        });
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }

    toggleMeasurement(measurement: Selected<MLMeasurement>): void {
        measurement.selected = !measurement.selected;
        this.selectedMeasurements = this.selectedMeasurements.filter((selectedMeasurement: MLMeasurement) => selectedMeasurement.id !== measurement.child.id);
        if (measurement.selected) {
            this.selectedMeasurements.push(measurement.child);
        }
        if (!this.selectedMeasurements.length) {
            this.useMyWorkflow = false;
        }
    }

    requestMlMeasurement(): void {
        const modalRef = this.modalService.open(ContactUsComponent);
        const modalComponent = modalRef.componentInstance as ContactUsComponent;
        modalComponent.title = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.TITLE');
        modalComponent.description = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION');
        modalComponent.messageLabel = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION_OF_REQUEST');
        modalComponent.messagePlaceholder = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION_OF_REQUEST_PLACEHOLDER');
        modalComponent.buttonText = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SEND_REQUEST');
        modalComponent.selectedSupportType = SupportTypes.RequestMlMeasurement;
        modalComponent.successModalTitle = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SUCCESS_MODAL.TITLE');
        modalComponent.successModalMessage = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SUCCESS_MODAL.MESSAGE');
    }

    hasPermissions(requiredPermissions: string[]): boolean {
        return this.userService.hasPermissions(requiredPermissions);
    }
}
